import { useEffect, useRef, useCallback } from 'react';

export type AdaptiveChatScrollOptions = {
  thresholdPx?: number;
};

/**
 * useAdaptiveChatScroll
 *
 * Modern chat auto-scroll behavior:
 * - Auto-scrolls smoothly when user is at/near bottom
 * - STOPS auto-scroll when user scrolls UP (wheel/touch)
 * - Resumes auto-scroll when user manually scrolls back to bottom
 * - Works during streaming with smooth animation
 */
export default function useAdaptiveChatScroll(
  containerRef: React.RefObject<HTMLElement> | React.MutableRefObject<HTMLElement | null> | { current: HTMLElement | null },
  messages: any[],
  isStreaming?: boolean,
  options?: AdaptiveChatScrollOptions
): void {
  const thresholdPx = options?.thresholdPx ?? 50;

  // Track if auto-scroll should be active
  const shouldAutoScrollRef = useRef<boolean>(true);

  // Track if we're currently programmatically scrolling (to ignore those events)
  const isProgrammaticScrollRef = useRef<boolean>(false);

  // Animation frame ID for cleanup
  const rafIdRef = useRef<number | null>(null);

  // Last scroll position to detect direction
  const lastScrollTopRef = useRef<number>(0);

  // Check if container is at bottom
  const isAtBottom = useCallback((el: HTMLElement): boolean => {
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= thresholdPx;
  }, [thresholdPx]);

  // Smooth scroll to bottom
  const scrollToBottom = useCallback((el: HTMLElement, instant = false) => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = true;

      const targetScrollTop = el.scrollHeight - el.clientHeight;

      if (instant) {
        el.scrollTop = targetScrollTop;
      } else {
        el.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      }

      // Reset programmatic flag after scroll completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        lastScrollTopRef.current = el.scrollTop;
      }, 100);
    });
  }, []);

  // Handle user scroll events (wheel and touch)
  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;

    // Detect user scroll UP via wheel
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        // User scrolled UP - disable auto-scroll
        shouldAutoScrollRef.current = false;
      } else if (e.deltaY > 0 && isAtBottom(el)) {
        // User scrolled DOWN and reached bottom - re-enable auto-scroll
        shouldAutoScrollRef.current = true;
      }
    };

    // Detect user scroll via touch (mobile)
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? 0;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touchY = e.touches[0]?.clientY ?? 0;
      const deltaY = touchStartY - touchY;

      if (deltaY < -10) {
        // User swiped DOWN (scrolling UP) - disable auto-scroll
        shouldAutoScrollRef.current = false;
      }
      touchStartY = touchY;
    };

    // Also handle manual scrollbar drag
    const handleScroll = () => {
      // Skip if this is a programmatic scroll
      if (isProgrammaticScrollRef.current) return;

      const currentScrollTop = el.scrollTop;
      const scrolledUp = currentScrollTop < lastScrollTopRef.current - 5;

      if (scrolledUp) {
        shouldAutoScrollRef.current = false;
      } else if (isAtBottom(el)) {
        // User scrolled to bottom - re-enable
        shouldAutoScrollRef.current = true;
      }

      lastScrollTopRef.current = currentScrollTop;
    };

    el.addEventListener('wheel', handleWheel, { passive: true });
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('scroll', handleScroll, { passive: true });

    // Initialize
    lastScrollTopRef.current = el.scrollTop;
    shouldAutoScrollRef.current = isAtBottom(el);

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef, isAtBottom]);

  // Scroll on new messages
  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el || !shouldAutoScrollRef.current) return;

    // Use instant scroll for new messages to ensure they're visible immediately
    scrollToBottom(el, true);
  }, [containerRef, messages.length, scrollToBottom]);

  // Handle streaming: observe content changes in last message
  useEffect(() => {
    const container = containerRef.current as HTMLElement | null;
    if (!container || !isStreaming) return;

    let mutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let scrollInterval: ReturnType<typeof setInterval> | null = null;

    // Observe the last message element for size/content changes
    const setupObservers = () => {
      const lastMessage = container.lastElementChild;
      if (!lastMessage) return;

      // ResizeObserver for element size changes
      resizeObserver = new ResizeObserver(() => {
        if (shouldAutoScrollRef.current) {
          scrollToBottom(container, true);
        }
      });
      resizeObserver.observe(lastMessage);

      // MutationObserver for text content changes (letter-by-letter typing)
      mutationObserver = new MutationObserver(() => {
        if (shouldAutoScrollRef.current) {
          scrollToBottom(container, true);
        }
      });
      mutationObserver.observe(lastMessage, {
        subtree: true,
        characterData: true,
        childList: true
      });
    };

    setupObservers();

    // Backup: periodic scroll during streaming (handles edge cases)
    scrollInterval = setInterval(() => {
      if (shouldAutoScrollRef.current && container) {
        const targetScrollTop = container.scrollHeight - container.clientHeight;
        // Only scroll if not already at bottom (avoid unnecessary calls)
        if (Math.abs(container.scrollTop - targetScrollTop) > 2) {
          container.scrollTop = targetScrollTop;
        }
      }
    }, 100); // 100ms is enough, not too aggressive

    return () => {
      if (mutationObserver) mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [containerRef, isStreaming, scrollToBottom]);

  // Handle window resize
  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;

    const handleResize = () => {
      if (shouldAutoScrollRef.current) {
        scrollToBottom(el, true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [containerRef, scrollToBottom]);

  // Initial mount: scroll to bottom
  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;

    // Immediate scroll on mount
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight - el.clientHeight;
      shouldAutoScrollRef.current = true;
      lastScrollTopRef.current = el.scrollTop;
    });
  }, [containerRef]);

  // Re-enable auto-scroll when streaming starts (new bot response)
  useEffect(() => {
    if (isStreaming) {
      const el = containerRef.current as HTMLElement | null;
      if (el && isAtBottom(el)) {
        shouldAutoScrollRef.current = true;
      }
    }
  }, [isStreaming, containerRef, isAtBottom]);

  // Preserve scroll position when streaming ends
  // This prevents the page from jumping when the final message replaces the streaming one
  const wasStreamingRef = useRef<boolean>(false);
  const savedScrollTopRef = useRef<number>(0);
  const savedAutoScrollStateRef = useRef<boolean>(true);

  // Continuously save scroll position during streaming
  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el || !isStreaming) return;

    // Save position periodically during streaming
    const saveInterval = setInterval(() => {
      savedScrollTopRef.current = el.scrollTop;
      savedAutoScrollStateRef.current = shouldAutoScrollRef.current;
    }, 50);

    return () => clearInterval(saveInterval);
  }, [isStreaming, containerRef]);

  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;

    if (isStreaming && !wasStreamingRef.current) {
      // Streaming just started
      wasStreamingRef.current = true;
      savedScrollTopRef.current = el.scrollTop;
      savedAutoScrollStateRef.current = shouldAutoScrollRef.current;
    } else if (!isStreaming && wasStreamingRef.current) {
      // Streaming just ended
      wasStreamingRef.current = false;

      // If user had scrolled up (auto-scroll was disabled), restore their position
      // after React re-renders with the final message
      if (!savedAutoScrollStateRef.current && savedScrollTopRef.current > 0) {
        // Use multiple rAF to ensure we're after React's DOM updates
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (el) {
              isProgrammaticScrollRef.current = true;
              el.scrollTop = savedScrollTopRef.current;
              // Also restore the auto-scroll state
              shouldAutoScrollRef.current = false;
              setTimeout(() => {
                isProgrammaticScrollRef.current = false;
                lastScrollTopRef.current = el.scrollTop;
              }, 50);
            }
          });
        });
      }
    }
  }, [isStreaming, containerRef]);
}
